import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useIntl } from "../contexts/IntlContext";
import {
  fetchChannelCities,
  fetchChannelStates,
} from "../services/channelLocations";


const SEARCH_DEBOUNCE_MS = 300;

type UseLocationFilterOptionsArgs = {
  country?: string;
  enabled: boolean;
  /** Estados já escolhidos: restringem as cidades oferecidas. */
  states: string[];
};

/**
 * Opções dos filtros de estado/cidade do dashboard. Estados carregam uma vez (lista curta);
 * cidades só são buscadas quando o campo abre ou o usuário digita (`q`, com debounce), uma
 * requisição por estado selecionado — nunca a lista inteira no carregamento da tela.
 */
export function useLocationFilterOptions({
  country,
  enabled,
  states: selectedStates,
}: UseLocationFilterOptionsArgs) {
  const intl = useIntl();
  const [states, setStates] = useState<string[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [cities, setCities] = useState<string[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesOpen, setCitiesOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const statesKey = selectedStates.join(",");
  const statesRef = useRef(selectedStates);
  statesRef.current = selectedStates;

  useEffect(() => {
    if (!enabled) return;
    let canceled = false;
    setStatesLoading(true);
    fetchChannelStates(country)
      .then((list) => {
        if (!canceled) setStates(list);
      })
      .catch(() => {
        if (canceled) return;
        setStates([]);
        toast.error(intl.formatMessage({ id: "messages.connectionError" }));
      })
      .finally(() => {
        if (!canceled) setStatesLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [country, enabled, intl]);

  useEffect(() => {
    if (!enabled || !citiesOpen) return;
    const controller = new AbortController();
    const timer = window.setTimeout(
      async () => {
        setCitiesLoading(true);
        try {
          const q = citySearch.trim() || undefined;
          const scopes: (string | undefined)[] = statesRef.current.length
            ? statesRef.current
            : [undefined];
          const results = await Promise.all(
            scopes.map((state) =>
              fetchChannelCities({ country, state, q, signal: controller.signal }),
            ),
          );
          if (controller.signal.aborted) return;
          const merged = Array.from(
            new Set(results.flat()),
          ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
          setCities(merged);
        } catch (err) {
          if (!controller.signal.aborted && !axios.isCancel(err)) {
            setCities([]);
            toast.error(intl.formatMessage({ id: "messages.connectionError" }));
          }
        } finally {
          if (!controller.signal.aborted) setCitiesLoading(false);
        }
      },
      citySearch ? SEARCH_DEBOUNCE_MS : 0,
    );
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [country, enabled, citiesOpen, citySearch, statesKey, intl]);

  const onCitiesInputChange = useCallback(
    (_: unknown, value: string, reason: string) => {
      setCitySearch(reason === "input" ? value : "");
    },
    [],
  );

  return {
    states,
    statesLoading,
    cities,
    citiesLoading,
    onCitiesOpen: () => setCitiesOpen(true),
    onCitiesClose: () => setCitiesOpen(false),
    onCitiesInputChange,
  };
}
